/*!
 * devextreme-angular
 * Version: 19.1.3
 * Build date: Tue May 14 2019
 *
 * Copyright (c) 2012 - 2019 Developer Express Inc. ALL RIGHTS RESERVED
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file in the root of the project for details.
 *
 * https://github.com/DevExpress/devextreme-angular
 */

/* tslint:disable:max-line-length */











import { BrowserTransferStateModule } from '@angular/platform-browser';
import { TransferState } from '@angular/platform-browser';

import {
    Component,
    NgModule,
    ElementRef,
    NgZone,
    PLATFORM_ID,
    Inject,

    Input,
    Output,
    OnDestroy,
    EventEmitter,
    OnInit,
    AfterViewInit,
    ContentChild,
    forwardRef,
    HostListener,
    OnChanges,
    DoCheck,
    SimpleChanges,
    ContentChildren,
    QueryList
} from '@angular/core';


import DevExpress from 'devextreme/bundles/dx.all';

import DxNumberBox from 'devextreme/ui/number_box';

import { DxValidatorComponent } from './validator';

import {
    ControlValueAccessor,
    NG_VALUE_ACCESSOR
} from '@angular/forms';

import { DxComponent } from '../core/component';
import { DxTemplateHost } from '../core/template-host';
import { DxIntegrationModule } from '../core/integration';
import { DxTemplateModule } from '../core/template';
import { NestedOptionHost } from '../core/nested-option';
import { WatcherHelper } from '../core/watcher-helper';
import { IterableDifferHelper } from '../core/iterable-differ-helper';

import { DxiButtonModule } from './nested/button-dxi';
import { DxoOptionsModule } from './nested/options';
import { DxoFormatModule } from './nested/format';

import { DxiButtonComponent } from './nested/button-dxi';




const CUSTOM_VALUE_ACCESSOR_PROVIDER = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => DxNumberBoxComponent),
    multi: true
};

/**
 * The NumberBox is a widget that displays a numeric value and allows a user to modify it by typing in a value, and incrementing or decrementing it using the keyboard or mouse.
 */
@Component({
    selector: 'dx-number-box',
    template: '',
    providers: [
        DxTemplateHost,
        WatcherHelper,
        CUSTOM_VALUE_ACCESSOR_PROVIDER,
        NestedOptionHost,
        IterableDifferHelper
    ]
})
export class DxNumberBoxComponent extends DxComponent implements OnDestroy, OnInit, AfterViewInit, ControlValueAccessor, OnChanges, DoCheck {
    instance: DxNumberBox;

    @ContentChild(DxValidatorComponent)
    validator: DxValidatorComponent;


    /**
     * Specifies the shortcut key that sets focus on the widget.
     */
    @Input()
    get accessKey(): string {
        return this._getOption('accessKey');
    }
    set accessKey(value: string) {
        this._setOption('accessKey', value);
    }


    /**
     * Specifies whether or not the widget changes its state when interacting with a user.
     */
    @Input()
    get activeStateEnabled(): boolean {
        return this._getOption('activeStateEnabled');
    }
    set activeStateEnabled(value: boolean) {
        this._setOption('activeStateEnabled', value);
    }


    /**
     * Allows you to add custom buttons to the input text field.
     */
    @Input()
    get buttons(): Array<string | DevExpress.ui.dxTextEditorButton> {
        return this._getOption('buttons');
    }
    set buttons(value: Array<string | DevExpress.ui.dxTextEditorButton>) {
        this._setOption('buttons', value);
    }


    /**
     * Specifies whether the widget responds to user interaction.
     */
    @Input()
    get disabled(): boolean {
        return this._getOption('disabled');
    }
    set disabled(value: boolean) {
        this._setOption('disabled', value);
    }


    /**
     * Specifies the attributes to be attached to the widget's root element.
     */
    @Input()
    get elementAttr(): any {
        return this._getOption('elementAttr');
    }
    set elementAttr(value: any) {
        this._setOption('elementAttr', value);
    }


    /**
     * Specifies whether the widget can be focused using keyboard navigation.
     */
    @Input()
    get focusStateEnabled(): boolean {
        return this._getOption('focusStateEnabled');
    }
    set focusStateEnabled(value: boolean) {
        this._setOption('focusStateEnabled', value);
    }


    /**
     * Specifies the value's display format and controls user input accordingly.
     */
    @Input()
    get format(): DevExpress.ui.format | string {
        return this._getOption('format');
    }
    set format(value: DevExpress.ui.format | string) {
        this._setOption('format', value);
    }


    /**
     * Specifies the widget's height.
     */
    @Input()
    get height(): number | Function | string {
        return this._getOption('height');
    }
    set height(value: number | Function | string) {
        this._setOption('height', value);
    }


    /**
     * Specifies text for a hint that appears when a user pauses on the widget.
     */
    @Input()
    get hint(): string {
        return this._getOption('hint');
    }
    set hint(value: string) {
        this._setOption('hint', value);
    }


    /**
     * Specifies whether the widget changes its state when a user pauses on it.
     */
    @Input()
    get hoverStateEnabled(): boolean {
        return this._getOption('hoverStateEnabled');
    }
    set hoverStateEnabled(value: boolean) {
        this._setOption('hoverStateEnabled', value);
    }


    /**
     * Specifies the attributes to be passed on to the underlying HTML element.
     */
    @Input()
    get inputAttr(): any {
        return this._getOption('inputAttr');
    }
    set inputAttr(value: any) {
        this._setOption('inputAttr', value);
    }


    /**
     * Specifies the text of the message displayed if the specified value is not a number.
     */
    @Input()
    get invalidValueMessage(): string {
        return this._getOption('invalidValueMessage');
    }
    set invalidValueMessage(value: string) {
        this._setOption('invalidValueMessage', value);
    }


    /**
     * Specifies whether the editor's value is valid.
     */
    @Input()
    get isValid(): boolean {
        return this._getOption('isValid');
    }
    set isValid(value: boolean) {
        this._setOption('isValid', value);
    }


    /**
     * The maximum value accepted by the number box.
     */
    @Input()
    get max(): number {
        return this._getOption('max');
    }
    set max(value: number) {
        this._setOption('max', value);
    }


    /**
     * The minimum value accepted by the number box.
     */
    @Input()
    get min(): number {
        return this._getOption('min');
    }
    set min(value: number) {
        this._setOption('min', value);
    }


    /**
     * Specifies the value to be passed to the type attribute of the underlying `` element.
     */
    @Input()
    get mode(): string {
        return this._getOption('mode');
    }
    set mode(value: string) {
        this._setOption('mode', value);
    }


    /**
     * The value to be assigned to the `name` attribute of the underlying HTML element.
     */
    @Input()
    get name(): string {
        return this._getOption('name');
    }
    set name(value: string) {
        this._setOption('name', value);
    }


    /**
     * The text displayed by the widget when the widget value is empty.
     */
    @Input()
    get placeholder(): string {
        return this._getOption('placeholder');
    }
    set placeholder(value: string) {
        this._setOption('placeholder', value);
    }


    /**
     * A Boolean value specifying whether or not the widget is read-only.
     */
    @Input()
    get readOnly(): boolean {
        return this._getOption('readOnly');
    }
    set readOnly(value: boolean) {
        this._setOption('readOnly', value);
    }


    /**
     * Switches the widget to a right-to-left representation.
     */
    @Input()
    get rtlEnabled(): boolean {
        return this._getOption('rtlEnabled');
    }
    set rtlEnabled(value: boolean) {
        this._setOption('rtlEnabled', value);
    }


    /**
     * Specifies whether to display the Clear button in the widget.
     */
    @Input()
    get showClearButton(): boolean {
        return this._getOption('showClearButton');
    }
    set showClearButton(value: boolean) {
        this._setOption('showClearButton', value);
    }


    /**
     * Specifies whether to show the buttons that change the value by a step.
     */
    @Input()
    get showSpinButtons(): boolean {
        return this._getOption('showSpinButtons');
    }
    set showSpinButtons(value: boolean) {
        this._setOption('showSpinButtons', value);
    }


    /**
     * Specifies how much the widget's value changes when using the spin buttons, Up/Down arrow keys, or mouse wheel.
     */
    @Input()
    get step(): number {
        return this._getOption('step');
    }
    set step(value: number) {
        this._setOption('step', value);
    }


    /**
     * Specifies how the widget's text field is styled.
     */
    @Input()
    get stylingMode(): string {
        return this._getOption('stylingMode');
    }
    set stylingMode(value: string) {
        this._setOption('stylingMode', value);
    }


    /**
     * Specifies the number of the element when the Tab key is used for navigating.
     */
    @Input()
    get tabIndex(): number {
        return this._getOption('tabIndex');
    }
    set tabIndex(value: number) {
        this._setOption('tabIndex', value);
    }


    /**
     * The read-only option that holds the text displayed by the widget input element.
     */
    @Input()
    get text(): string {
        return this._getOption('text');
    }
    set text(value: string) {
        this._setOption('text', value);
    }


    /**
     * Specifies whether to use touch friendly spin buttons. Applies only if showSpinButtons is true.
     */
    @Input()
    get useLargeSpinButtons(): boolean {
        return this._getOption('useLargeSpinButtons');
    }
    set useLargeSpinButtons(value: boolean) {
        this._setOption('useLargeSpinButtons', value);
    }


    /**
     * Specifies information on the validation error when using a custom validation engine. Should be changed at runtime along with the isValid option.
     */
    @Input()
    get validationError(): any {
        return this._getOption('validationError');
    }
    set validationError(value: any) {
        this._setOption('validationError', value);
    }


    /**
     * Specifies how the message about the validation rules that are not satisfied by this editor's value is displayed.
     */
    @Input()
    get validationMessageMode(): string {
        return this._getOption('validationMessageMode');
    }
    set validationMessageMode(value: string) {
        this._setOption('validationMessageMode', value);
    }


    /**
     * The current number box value.
     */
    @Input()
    get value(): number {
        return this._getOption('value');
    }
    set value(value: number) {
        this._setOption('value', value);
    }


    /**
     * Specifies the DOM events after which the widget's value should be updated.
     */
    @Input()
    get valueChangeEvent(): string {
        return this._getOption('valueChangeEvent');
    }
    set valueChangeEvent(value: string) {
        this._setOption('valueChangeEvent', value);
    }


    /**
     * Specifies whether the widget is visible.
     */
    @Input()
    get visible(): boolean {
        return this._getOption('visible');
    }
    set visible(value: boolean) {
        this._setOption('visible', value);
    }


    /**
     * Specifies the widget's width.
     */
    @Input()
    get width(): number | Function | string {
        return this._getOption('width');
    }
    set width(value: number | Function | string) {
        this._setOption('width', value);
    }

    /**
     * A function that is executed when the widget loses focus after the text field's content was changed using the keyboard.
     */
    @Output() onChange: EventEmitter<any>;

    /**
     * A function that is executed when the widget's content is ready and each time the content is changed.
     */
    @Output() onContentReady: EventEmitter<any>;

    /**
     * A function that is executed when the widget's input has been copied.
     */
    @Output() onCopy: EventEmitter<any>;

    /**
     * A function that is executed when the widget's input has been cut.
     */
    @Output() onCut: EventEmitter<any>;

    /**
     * A function that is executed before the widget is disposed of.
     */
    @Output() onDisposing: EventEmitter<any>;

    /**
     * A function that is executed when the Enter key has been pressed while the widget is focused.
     */
    @Output() onEnterKey: EventEmitter<any>;

    /**
     * A function that is executed when the widget gets focus.
     */
    @Output() onFocusIn: EventEmitter<any>;

    /**
     * A function that is executed when the widget loses focus.
     */
    @Output() onFocusOut: EventEmitter<any>;

    /**
     * A function that is executed only once, after the widget is initialized.
     */
    @Output() onInitialized: EventEmitter<any>;

    /**
     * A function that is executed each time the widget's input is changed while the widget is focused.
     */
    @Output() onInput: EventEmitter<any>;

    /**
     * A function that is executed when a user is pressing a key on the keyboard.
     */
    @Output() onKeyDown: EventEmitter<any>;

    /**
     * A function that is executed when a user presses a key on the keyboard.
     */
    @Output() onKeyPress: EventEmitter<any>;

    /**
     * A function that is executed when a user releases a key on the keyboard.
     */
    @Output() onKeyUp: EventEmitter<any>;

    /**
     * A function that is executed after a widget option is changed.
     */
    @Output() onOptionChanged: EventEmitter<any>;

    /**
     * A function that is executed when the widget's input has been pasted.
     */
    @Output() onPaste: EventEmitter<any>;

    /**
     * A function that is executed after the widget's value is changed.
     */
    @Output() onValueChanged: EventEmitter<any>;

    /**
     * A handler for the accessKeyChange event.
     */
    @Output() accessKeyChange: EventEmitter<string>;

    /**
     * A handler for the activeStateEnabledChange event.
     */
    @Output() activeStateEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the buttonsChange event.
     */
    @Output() buttonsChange: EventEmitter<Array<string | DevExpress.ui.dxTextEditorButton>>;

    /**
     * A handler for the disabledChange event.
     */
    @Output() disabledChange: EventEmitter<boolean>;

    /**
     * A handler for the elementAttrChange event.
     */
    @Output() elementAttrChange: EventEmitter<any>;

    /**
     * A handler for the focusStateEnabledChange event.
     */
    @Output() focusStateEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the formatChange event.
     */
    @Output() formatChange: EventEmitter<DevExpress.ui.format | string>;

    /**
     * A handler for the heightChange event.
     */
    @Output() heightChange: EventEmitter<number | Function | string>;

    /**
     * A handler for the hintChange event.
     */
    @Output() hintChange: EventEmitter<string>;

    /**
     * A handler for the hoverStateEnabledChange event.
     */
    @Output() hoverStateEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the inputAttrChange event.
     */
    @Output() inputAttrChange: EventEmitter<any>;

    /**
     * A handler for the invalidValueMessageChange event.
     */
    @Output() invalidValueMessageChange: EventEmitter<string>;

    /**
     * A handler for the isValidChange event.
     */
    @Output() isValidChange: EventEmitter<boolean>;

    /**
     * A handler for the maxChange event.
     */
    @Output() maxChange: EventEmitter<number>;

    /**
     * A handler for the minChange event.
     */
    @Output() minChange: EventEmitter<number>;

    /**
     * A handler for the modeChange event.
     */
    @Output() modeChange: EventEmitter<string>;

    /**
     * A handler for the nameChange event.
     */
    @Output() nameChange: EventEmitter<string>;

    /**
     * A handler for the placeholderChange event.
     */
    @Output() placeholderChange: EventEmitter<string>;

    /**
     * A handler for the readOnlyChange event.
     */
    @Output() readOnlyChange: EventEmitter<boolean>;

    /**
     * A handler for the rtlEnabledChange event.
     */
    @Output() rtlEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the showClearButtonChange event.
     */
    @Output() showClearButtonChange: EventEmitter<boolean>;

    /**
     * A handler for the showSpinButtonsChange event.
     */
    @Output() showSpinButtonsChange: EventEmitter<boolean>;

    /**
     * A handler for the stepChange event.
     */
    @Output() stepChange: EventEmitter<number>;

    /**
     * A handler for the stylingModeChange event.
     */
    @Output() stylingModeChange: EventEmitter<string>;

    /**
     * A handler for the tabIndexChange event.
     */
    @Output() tabIndexChange: EventEmitter<number>;

    /**
     * A handler for the textChange event.
     */
    @Output() textChange: EventEmitter<string>;

    /**
     * A handler for the useLargeSpinButtonsChange event.
     */
    @Output() useLargeSpinButtonsChange: EventEmitter<boolean>;

    /**
     * A handler for the validationErrorChange event.
     */
    @Output() validationErrorChange: EventEmitter<any>;

    /**
     * A handler for the validationMessageModeChange event.
     */
    @Output() validationMessageModeChange: EventEmitter<string>;

    /**
     * A handler for the valueChange event.
     */
    @Output() valueChange: EventEmitter<number>;

    /**
     * A handler for the valueChangeEventChange event.
     */
    @Output() valueChangeEventChange: EventEmitter<string>;

    /**
     * A handler for the visibleChange event.
     */
    @Output() visibleChange: EventEmitter<boolean>;

    /**
     * A handler for the widthChange event.
     */
    @Output() widthChange: EventEmitter<number | Function | string>;

    @Output() onBlur: EventEmitter<any>;


    @HostListener('valueChange', ['$event']) change(_) { }
    @HostListener('onBlur', ['$event']) touched = (_) => {};


    @ContentChildren(DxiButtonComponent)
    get buttonsChildren(): QueryList<DxiButtonComponent> {
        return this._getOption('buttons');
    }
    set buttonsChildren(value) {
        this.setChildren('buttons', value);
    }


    constructor(elementRef: ElementRef, ngZone: NgZone, templateHost: DxTemplateHost,
            private _watcherHelper: WatcherHelper,
            private _idh: IterableDifferHelper, optionHost: NestedOptionHost,
            transferState: TransferState,
            @Inject(PLATFORM_ID) platformId: any) {

        super(elementRef, ngZone, templateHost, _watcherHelper, transferState, platformId);

        this._createEventEmitters([
            { subscribe: 'change', emit: 'onChange' },
            { subscribe: 'contentReady', emit: 'onContentReady' },
            { subscribe: 'copy', emit: 'onCopy' },
            { subscribe: 'cut', emit: 'onCut' },
            { subscribe: 'disposing', emit: 'onDisposing' },
            { subscribe: 'enterKey', emit: 'onEnterKey' },
            { subscribe: 'focusIn', emit: 'onFocusIn' },
            { subscribe: 'focusOut', emit: 'onFocusOut' },
            { subscribe: 'initialized', emit: 'onInitialized' },
            { subscribe: 'input', emit: 'onInput' },
            { subscribe: 'keyDown', emit: 'onKeyDown' },
            { subscribe: 'keyPress', emit: 'onKeyPress' },
            { subscribe: 'keyUp', emit: 'onKeyUp' },
            { subscribe: 'optionChanged', emit: 'onOptionChanged' },
            { subscribe: 'paste', emit: 'onPaste' },
            { subscribe: 'valueChanged', emit: 'onValueChanged' },
            { emit: 'accessKeyChange' },
            { emit: 'activeStateEnabledChange' },
            { emit: 'buttonsChange' },
            { emit: 'disabledChange' },
            { emit: 'elementAttrChange' },
            { emit: 'focusStateEnabledChange' },
            { emit: 'formatChange' },
            { emit: 'heightChange' },
            { emit: 'hintChange' },
            { emit: 'hoverStateEnabledChange' },
            { emit: 'inputAttrChange' },
            { emit: 'invalidValueMessageChange' },
            { emit: 'isValidChange' },
            { emit: 'maxChange' },
            { emit: 'minChange' },
            { emit: 'modeChange' },
            { emit: 'nameChange' },
            { emit: 'placeholderChange' },
            { emit: 'readOnlyChange' },
            { emit: 'rtlEnabledChange' },
            { emit: 'showClearButtonChange' },
            { emit: 'showSpinButtonsChange' },
            { emit: 'stepChange' },
            { emit: 'stylingModeChange' },
            { emit: 'tabIndexChange' },
            { emit: 'textChange' },
            { emit: 'useLargeSpinButtonsChange' },
            { emit: 'validationErrorChange' },
            { emit: 'validationMessageModeChange' },
            { emit: 'valueChange' },
            { emit: 'valueChangeEventChange' },
            { emit: 'visibleChange' },
            { emit: 'widthChange' },
            { emit: 'onBlur' }
        ]);

        this._idh.setHost(this);
        optionHost.setHost(this);
    }

    protected _createInstance(element, options) {
        return new DxNumberBox(element, options);
    }

    writeValue(value: any): void {
        this.eventHelper.lockedValueChangeEvent = true;
        this.value = value;
        this.eventHelper.lockedValueChangeEvent = false;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    registerOnChange(fn: (_: any) => void): void { this.change = fn; }
    registerOnTouched(fn: () => void): void { this.touched = fn; }

    _createWidget(element: any) {
        super._createWidget(element);
        this.instance.on('focusOut', (e) => {
            this.eventHelper.fireNgEvent('onBlur', [e]);
        });
    }

    ngOnDestroy() {
        this._destroyWidget();
    }

    ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);
        this.setupChanges('buttons', changes);
    }

    setupChanges(prop: string, changes: SimpleChanges) {
        if (!(prop in this._optionsToUpdate)) {
            this._idh.setup(prop, changes);
        }
    }

    ngDoCheck() {
        this._idh.doCheck('buttons');
        this._watcherHelper.checkWatchers();
        super.ngDoCheck();
        super.clearChangedOptions();
    }

    _setOption(name: string, value: any) {
        let isSetup = this._idh.setupSingle(name, value);
        let isChanged = this._idh.getChanges(name, value) !== null;

        if (isSetup || isChanged) {
            super._setOption(name, value);
        }
    }

    ngOnInit() {
        super.ngOnInit();
        if (this.validator) {
            this.validator.createInstanceOnInit = false;
        }
    }
    ngAfterViewInit() {
        super.ngAfterViewInit();
        if (this.validator) {
            this.validator.createInstance(this.element.nativeElement);
        }
    }
}

@NgModule({
  imports: [
    DxiButtonModule,
    DxoOptionsModule,
    DxoFormatModule,
    DxIntegrationModule,
    DxTemplateModule,
    BrowserTransferStateModule
  ],
  declarations: [
    DxNumberBoxComponent
  ],
  exports: [
    DxNumberBoxComponent,
    DxiButtonModule,
    DxoOptionsModule,
    DxoFormatModule,
    DxTemplateModule
  ]
})
export class DxNumberBoxModule { }
